import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FloatingFocusManager,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  hide,
  offset,
  shift,
  size,
  useFloating,
} from "@floating-ui/react";
import type { Middleware, MiddlewareState } from "@floating-ui/react";
import { XIcon } from "@heroicons/react/outline";
import { getStepPosition, isAwaitStep } from "../registry";
import type { AnchorTourStep, TourDefinition } from "../registry/types";
import type { StepPositionOffset } from "../state/onboardingSlice";
import { ONBOARDING_PRIMARY_BUTTON, ONBOARDING_TEXT_BUTTON } from "../components/buttonClasses";

const KEEP_OUT_PADDING = 8;

const clipToViewport = (rect: DOMRect): DOMRect => {
  const left = Math.max(rect.left, 0);
  const top = Math.max(rect.top, 0);
  const right = Math.min(rect.right, window.innerWidth);
  const bottom = Math.min(rect.bottom, window.innerHeight);
  if (right <= left || bottom <= top) return rect;
  return new DOMRect(left, top, right - left, bottom - top);
};

const keepOutBarsBelow = ({ x, y, rects, elements }: MiddlewareState): DOMRect[] => {
  const reference =
    elements.reference instanceof Element
      ? elements.reference
      : elements.reference.contextElement ?? null;
  return Array.from(document.querySelectorAll<HTMLElement>("[data-tour-keep-out]"))
    .filter((bar) => !(reference && bar.contains(reference)))
    .map((bar) => bar.getBoundingClientRect())
    .filter(
      (bar) =>
        bar.width > 0 &&
        bar.height > 0 &&
        bar.right > x &&
        bar.left < x + rects.floating.width &&
        bar.bottom > y &&
        bar.top < y + rects.floating.height,
    );
};

const yAboveBars = (state: MiddlewareState): number | null => {
  const bars = keepOutBarsBelow(state);
  if (bars.length === 0) return null;
  const top = Math.min(...bars.map((bar) => bar.top));
  const y = Math.max(KEEP_OUT_PADDING, top - KEEP_OUT_PADDING - state.rects.floating.height);
  const { reference, floating } = state.rects;
  const coversReference =
    state.x < reference.x + reference.width &&
    reference.x < state.x + floating.width &&
    y < reference.y + reference.height &&
    reference.y < y + floating.height;
  return coversReference ? null : y;
};

const keepAboveBars = (): Middleware => ({
  name: "keepAboveBars",
  fn(state) {
    const y = yAboveBars(state);
    return y === null ? {} : { y };
  },
});

const ARROW_STATIC_SIDE = {
  top: "bottom",
  right: "left",
  bottom: "top",
  left: "right",
} as const;

export type TourPopoverProps = {
  target: HTMLElement;
  tour: TourDefinition;
  step: AnchorTourStep;
  stepIndex: number;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onHandoff: () => void;
  onReferenceHiddenChange?: (hidden: boolean) => void;
  positionOffset?: StepPositionOffset;
  hideCounter?: boolean;
  skippedSteps?: number[];
};

export const TourPopover = ({
  target,
  tour,
  step,
  stepIndex,
  onNext,
  onBack,
  onSkip,
  onHandoff,
  onReferenceHiddenChange,
  positionOffset = { before: 0, after: 0 },
  hideCounter = false,
  skippedSteps = [],
}: TourPopoverProps) => {
  const { t } = useTranslation();
  const previousStep = stepIndex > 0 ? tour.steps[stepIndex - 1] : undefined;
  const canGoBack =
    Boolean(previousStep) &&
    !(previousStep && isAwaitStep(previousStep)) &&
    !skippedSteps.includes(stepIndex - 1);
  const isLastStep = stepIndex === tour.steps.length - 1;
  const isActionStep = step.advanceOn === "targetClick";
  const isHintStep = Boolean(step.hint);
  const isModal = !isActionStep && !isHintStep;
  const isHandoffStep = Boolean(step.handoffTourId);
  const tourPosition = getStepPosition(tour, stepIndex, skippedSteps);
  const position = {
    current: tourPosition.current + positionOffset.before,
    total: tourPosition.total + positionOffset.before + positionOffset.after,
  };

  const titleId = useId();
  const bodyId = useId();
  const arrowRef = useRef<HTMLDivElement>(null);
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  const { refs, floatingStyles, context, middlewareData, placement } = useFloating({
    open: true,
    placement: step.placement ?? "bottom",
    strategy: "fixed",
    middleware: [
      offset(step.offset ?? 12),
      flip({ padding: 8, fallbackAxisSideDirection: "end" }),
      shift({ padding: 8, crossAxis: true }),
      size({
        padding: KEEP_OUT_PADDING,
        apply: (state) => {
          const bars = yAboveBars(state) === null ? [] : keepOutBarsBelow(state);
          const cappedHeight = bars.reduce(
            (height, bar) => Math.min(height, bar.top - 2 * KEEP_OUT_PADDING),
            state.availableHeight,
          );
          state.elements.floating.style.setProperty(
            "--tour-card-max-height",
            `${Math.max(200, Math.floor(cappedHeight))}px`,
          );
        },
      }),
      keepAboveBars(),
      arrow({ element: arrowRef }),
      hide(),
    ],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
  });

  useLayoutEffect(() => {
    refs.setReference(target);
    refs.setPositionReference({
      getBoundingClientRect: () => clipToViewport(target.getBoundingClientRect()),
      contextElement: target,
    });
  }, [refs, target]);

  const isHiddenByReference = middlewareData.hide?.referenceHidden ?? false;
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    setEntered(false);
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [tour.id, stepIndex]);
  const arrowSide = ARROW_STATIC_SIDE[placement.split("-")[0] as keyof typeof ARROW_STATIC_SIDE];
  const arrowX = middlewareData.arrow?.x;
  const arrowY = middlewareData.arrow?.y;

  useEffect(() => {
    onReferenceHiddenChange?.(isHiddenByReference);
  }, [isHiddenByReference, onReferenceHiddenChange, target]);

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={context}
        modal={isModal}
        initialFocus={isHintStep ? -1 : primaryButtonRef}
        returnFocus
      >
        <div
          ref={(node) => refs.setFloating(node)}
          role="dialog"
          aria-modal={isModal}
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          className={`z-[1110] ${step.wide ? "w-[420px]" : "w-[320px]"} focus:outline-none ${
            isHiddenByReference ? "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" : ""
          }`}
          style={isHiddenByReference ? undefined : floatingStyles}
        >
          <div
            ref={arrowRef}
            aria-hidden="true"
            className={`absolute h-3 w-3 rotate-45 bg-white border-2 border-green-500 transition-opacity duration-200 motion-reduce:transition-none ${
              entered ? "opacity-100" : "opacity-0"
            } ${isHiddenByReference ? "hidden" : ""}`}
            style={{
              left: arrowX !== undefined ? `${arrowX}px` : "",
              top: arrowY !== undefined ? `${arrowY}px` : "",
              [arrowSide]: "-6px",
            }}
          />
          <div
            data-testid="onboarding-tour-card"
            className={`relative max-h-[var(--tour-card-max-height,none)] overflow-y-auto rounded-xl bg-white p-5 ring-2 ring-green-500 shadow-[0_0_0_6px_rgba(0,200,137,0.15),0_20px_25px_-5px_rgba(0,0,0,0.12)] transition duration-200 ease-out motion-reduce:transition-none ${
              entered ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95"
            }`}
          >
            <button
              type="button"
              className="absolute top-3 right-3 h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
              onClick={onSkip}
              aria-label={t("onboarding.popover.close")}
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </button>
            <p
              className="pr-8 text-xs font-semibold uppercase tracking-wide text-green-800"
              aria-live="polite"
            >
              {hideCounter ? "\u00a0" : t("onboarding.popover.stepOf", position)}
            </p>
            <p id={titleId} className="mt-1 text-base font-semibold text-slate-900">
              {t(step.titleKey)}
            </p>
            <p id={bodyId} className="mt-1 text-sm leading-5 text-slate-600 whitespace-pre-line">
              {t(step.bodyKey)}
            </p>
            {isHandoffStep && (
              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  className={`${ONBOARDING_TEXT_BUTTON} text-slate-500 hover:text-slate-800`}
                  onClick={onNext}
                >
                  {t("onboarding.popover.later")}
                </button>
                <button
                  ref={primaryButtonRef}
                  type="button"
                  className={ONBOARDING_PRIMARY_BUTTON}
                  onClick={onHandoff}
                >
                  {t("onboarding.popover.showMe")}
                </button>
              </div>
            )}
            {isActionStep && !isHintStep && !isHandoffStep && canGoBack && (
              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  className={`${ONBOARDING_TEXT_BUTTON} text-slate-600 hover:text-slate-900`}
                  onClick={onBack}
                >
                  {t("general.back")}
                </button>
              </div>
            )}
            {!isHandoffStep && !isActionStep && !isHintStep && (
              <div className="mt-4 flex items-center justify-end gap-3">
                {canGoBack && (
                  <button
                    type="button"
                    className={`${ONBOARDING_TEXT_BUTTON} text-slate-600 hover:text-slate-900`}
                    onClick={onBack}
                  >
                    {t("general.back")}
                  </button>
                )}
                <button
                  ref={primaryButtonRef}
                  type="button"
                  className={ONBOARDING_PRIMARY_BUTTON}
                  onClick={onNext}
                >
                  {t(isLastStep ? "general.finish" : "general.next")}
                </button>
              </div>
            )}
          </div>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
};

export default TourPopover;
